import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CreatePartFromTruck } from "@/components/CreatePartFromTruck";
import { PullButton, canPullHere, pullOf } from "@/components/PullFromRow";
import { PendingComponentBadge } from "@/components/PendingComponentBadge";
import { ArrowLeft, Loader2, ClipboardCheck } from "lucide-react";
import { ConditionControl } from "@/components/ConditionControl";
import { ComponentWhere, ComponentListLine } from "@/components/ComponentWhere";
import { ComponentNotes } from "@/components/ComponentNotes";
import { ComponentSuggestions } from "@/components/ComponentSuggestions";

// ONE SCREEN, ALL TEN GROUPS, TAP TO SET. The knowledge lives under the truck: a tech standing
// there sees the cracked case, the missing engine, the pulled transmission. Ten separate flows is
// ten chances to walk away, so it is one list with the states on every row — and every tap saves
// by itself, so setting one and leaving is a complete, valid thing to do.
// C2 (30 Oct): the same one list as the desk — where each one is now, its notes, the AI's grey suggestions.
export function FloorTruckConditionCard({ donorId, stock, onBack }) {
  const [data, setData] = useState(null);
  const [pull, setPull] = useState(null);
  const { has } = useAuth();

  // C3 — the pull opens filled in: the component's values and photo come from the truck's own specs read
  const openPull = async (code) => {
    try {
      const { data: s } = await api.get(`/donors/${donorId}/specs`);
      const c = (s.components || []).find((x) => x.code === code);
      if (c) setPull(pullOf(c));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't open the pull."); }
  };

  const load = async () => {
    try { const r = await api.get(`/donors/${donorId}/condition`); setData(r.data); }
    catch { setData(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [donorId]);
  const apply = (res) => (res?.rows
    ? setData((d) => ({ ...d, rows: res.rows, summary: res.summary, one_list: res.one_list || d.one_list }))
    : load());

  if (data === null) return <div className="flex-1 flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-slate-400" /></div>;
  if (data === false) return (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 p-6" data-testid="floor-condition-error">
      <div className="text-xl font-black text-slate-700">Couldn’t load this truck’s components.</div>
      <button onClick={onBack} className="h-12 px-5 rounded-xl border-2 border-slate-200 font-bold">Back</button>
    </div>
  );

  return (
    <div className="flex-1 flex flex-col" data-testid="floor-truck-condition">
      <div className="p-4 sm:p-6 space-y-4">
        <button data-testid="floor-condition-back" onClick={onBack}
          className="flex items-center gap-1.5 font-bold text-[#B01E28] text-lg">
          <ArrowLeft className="w-5 h-5" /> Truck
        </button>

        <div>
          <div className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
            <ClipboardCheck className="w-6 h-6 text-[#B01E28]" />Rate components
          </div>
          <div className="font-mono text-base text-slate-500 mt-0.5">{stock || data.stock_number}</div>
          <div className="mt-1"><ComponentListLine list={data.one_list} anyBad={data.summary?.any_bad} big testid="floor-condition-summary" /></div>
        </div>

        {!data.can_assess && (
          <p data-testid="floor-condition-readonly" className="rounded-xl border-2 border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            You can see the ratings but not change them — that needs the “Rate component condition” permission.
          </p>
        )}

        <ComponentSuggestions donorId={donorId} items={data.one_list?.suggestions} answered={data.one_list?.answered}
          canAnswer={!!data.can_assess} via="floor" big onChanged={apply} testid="floor-suggestions" />

        <div className="space-y-3">
          {data.rows.map((r) => (
            <div key={r.component} data-testid={`floor-cond-block-${r.component}`}
              className="rounded-xl border-2 border-slate-200 bg-white p-3 space-y-2">
              <div className="font-display text-lg font-bold text-slate-800 flex items-center gap-2 flex-wrap">
                {r.label}<PendingComponentBadge show={r.pending_group} testid={`floor-cond-pending-${r.component}`} />
                <ComponentWhere where={r.where} big testid={`floor-where-${r.component}`} />
                {has("manage_parts") && canPullHere(r.where) && (
                  <span className="ml-auto"><PullButton big testid={`floor-pull-${r.component}`} onClick={() => openPull(r.component)} /></span>
                )}
              </div>
              <ConditionControl donorId={donorId} row={r} states={data.states} reasons={data.reasons}
                otherCode={data.other_code} canAssess={data.can_assess} big surface="floor"
                canDismiss={data.can_dismiss} dismissReasons={data.dismiss_reasons}
                onSaved={apply} />
              <ComponentNotes donorId={donorId} component={r.component} notes={r.notes} canAdd={!!data.can_assess}
                big testid={`floor-notes-${r.component}`} onClosed={load} />
            </div>
          ))}
        </div>
      </div>
      {pull && (
        <CreatePartFromTruck open={!!pull} onOpenChange={(o) => !o && setPull(null)} donorId={donorId}
          donorStock={stock || data.stock_number} big {...pull}
          onCreated={() => { setPull(null); load(); }} />
      )}
    </div>
  );
}
